import type { ReactNode } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { FESTIVAL_KIND_LABEL, labelOf } from "@/lib/tina/festivals";
import { FestivalStatusBadge } from "@/pages/tina/festivals/components/festival-status-badge";
import { headerSubLine } from "@/pages/tina/festivals/lib/page-copy";
import type { PageMode } from "@/pages/tina/festivals/lib/page-request";
import { LIST_PATH } from "@/pages/tina/festivals/lib/paths";
import type { FestivalPageRead } from "@/types/tina-festival-page";

interface PageHeaderProps {
  page: FestivalPageRead;
  mode: PageMode;
  /** The buttons and the Actions menu, on the right. */
  children?: ReactNode;
}

/** The festival's name with its type, its status and what the page says about it, and the buttons beside them. */
export function PageHeader({ page, mode, children }: PageHeaderProps) {
  return (
    <header className="flex flex-col gap-2">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>Tina</BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={LIST_PATH}>Festivals</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{page.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">{page.name}</h1>
            <Badge variant="outline" className="text-[10px] font-normal">
              {labelOf(FESTIVAL_KIND_LABEL, page.kind)}
            </Badge>
            <FestivalStatusBadge
              status={page.status}
              label={page.status_label}
            />
            {mode === "approve" ? (
              <Badge variant="outline" className="text-[10px] font-normal">
                Not in Mood yet
              </Badge>
            ) : null}
            {page.unpublished ? (
              <Badge variant="outline" className="text-[10px] font-normal">
                Unpublished
              </Badge>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">{headerSubLine(page)}</p>
          {page.backend_group_id ? (
            <Link
              to={`/event-groups/show/${page.backend_group_id}`}
              className="text-sm underline"
            >
              Open the group
            </Link>
          ) : null}
        </div>
        <div className="flex items-center gap-2">{children}</div>
      </div>
    </header>
  );
}
